import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  Save, 
  X, 
  ChevronDown, 
  ChevronRight, 
  Shield, 
  Check,
  Users,
  BookOpen,
  GraduationCap,
  Building,
  Calendar,
  FileText,
  DollarSign,
  Settings,
  Database,
  Award,
  UserCheck,
  Clock,
  Target,
  Lock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';

const CreateRolePage = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditing = Boolean(roleId);

  // États pour les données
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  
  // États pour l'interface
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Configuration des icônes par application
  const appIcons = {
    etudiant: Users,
    enseignant: GraduationCap,
    etablissement: Building,
    matiere: BookOpen,
    emplois: Calendar,
    assiduite: UserCheck,
    classe: Users,
    filiere: Target,
    niveau: Award,
    ponctualite: Clock,
    soutenance: Award,
    concours: Target,
    inscription: FileText,
    finance: DollarSign,
    examen: FileText,
    utilisateurs: Settings,
    controle: Database
  };

  // Configuration des noms d'affichage des applications
  const appDisplayNames = {
    etudiant: 'Étudiants',
    enseignant: 'Enseignants',
    etablissement: 'Établissement',
    matiere: 'Matières',
    emplois: 'Emplois du temps',
    assiduite: 'Assiduité',
    classe: 'Classes',
    filiere: 'Filières',
    niveau: 'Niveaux',
    ponctualite: 'Ponctualité',
    soutenance: 'Soutenances',
    concours: 'Concours',
    inscription: 'Inscriptions',
    finance: 'Finance',
    examen: 'Examens',
    utilisateurs: 'Utilisateurs',
    controle: 'Contrôles'
  };

  useEffect(() => {
    fetchPermissions();
    if (isEditing) {
      fetchRoleData();
    }
  }, [roleId]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/utilisateurs/permissions/all/');
      setAllPermissions(response.data);
      
      // Ouvrir le premier accordéon par défaut
      const firstApp = Object.keys(response.data)[0];
      if (firstApp) {
        setExpandedAccordions({ [firstApp]: true });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des permissions:', error);
      toast.error('Erreur lors du chargement des permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleData = async () => {
    try {
      const response = await api.get(`/utilisateurs/roles/${roleId}/`);
      const role = response.data;
      
      setFormData({
        name: role.name || '',
        description: role.description || ''
      });
      
      // Récupérer les permissions du rôle
      const permissionsResponse = await api.get(`/utilisateurs/roles/${roleId}/permissions/`);
      setSelectedPermissions(permissionsResponse.data.permissions || []);
    } catch (error) {
      console.error('Erreur lors du chargement du rôle:', error);
      toast.error('Erreur lors du chargement du rôle');
      navigate('/utilisateurs');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Effacer l'erreur si l'utilisateur corrige
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const toggleAccordion = (appKey) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [appKey]: !prev[appKey]
    }));
  };

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSelectAllApp = (appPermissions) => {
    const appPermissionIds = appPermissions.map(p => p.id);
    const allSelected = appPermissionIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      // Désélectionner toutes les permissions de cette app
      setSelectedPermissions(prev => 
        prev.filter(id => !appPermissionIds.includes(id))
      );
    } else {
      // Sélectionner toutes les permissions de cette app
      setSelectedPermissions(prev => {
        const newPermissions = [...prev];
        appPermissionIds.forEach(id => {
          if (!newPermissions.includes(id)) {
            newPermissions.push(id);
          }
        });
        return newPermissions;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Le nom du rôle est requis';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const roleData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: selectedPermissions
      };

      if (isEditing) {
        await api.put(`/utilisateurs/roles/${roleId}/`, roleData);
        toast.success('Rôle modifié avec succès');
      } else {
        await api.post('/utilisateurs/roles/', roleData);
        toast.success('Rôle créé avec succès');
      }
      
      navigate('/utilisateurs');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data?.name) {
        toast.error('Ce nom de rôle existe déjà');
      } else {
        toast.error(error.response?.data?.error || 'Une erreur est survenue');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getAppIcon = (appKey) => {
    const IconComponent = appIcons[appKey] || Shield;
    return IconComponent;
  };

  const getAppDisplayName = (appKey) => {
    return appDisplayNames[appKey] || appKey;
  };

  const getSelectedCount = (appPermissions) => {
    return appPermissions.filter(p => selectedPermissions.includes(p.id)).length;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* En-tête amélioré */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-6 lg:mb-0">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="p-2 bg-[#202654] rounded-lg">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {isEditing ? 'Modifier le rôle' : 'Créer un nouveau rôle'}
                  </h1>
                </div>
                <p className="text-lg text-gray-600 max-w-2xl">
                  {isEditing 
                    ? 'Modifiez les informations et permissions du rôle pour adapter les accès utilisateurs'
                    : 'Définissez un nouveau rôle avec ses permissions spécifiques pour contrôler l\'accès aux fonctionnalités'
                  }
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/utilisateurs')}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-all duration-200"
                >
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#1a1f45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {isEditing ? 'Modifier le rôle' : 'Créer le rôle'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informations de base améliorées */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#202654] to-[#1a1f45] px-8 py-6">
                <div className="flex items-center space-x-3">
                  <FileText className="h-6 w-6 text-white" />
                  <h2 className="text-xl font-semibold text-white">
                    Informations du rôle
                  </h2>
                </div>
                <p className="text-blue-100 mt-2">
                  Définissez les informations de base pour identifier ce rôle
                </p>
              </div>
              
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700">
                      Nom du rôle *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-[#202654] sm:text-sm transition-all duration-200"
                      placeholder="Ex: Administrateur, Enseignant, Étudiant..."
                      required
                    />
                    {formErrors.name && (
                      <div className="flex items-center space-x-2 mt-2">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <p className="text-sm text-red-600">{formErrors.name}</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="description" className="block text-sm font-semibold text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-[#202654] sm:text-sm transition-all duration-200 resize-none"
                      placeholder="Description du rôle et de ses responsabilités..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Permissions améliorées */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#202654] to-[#1a1f45] px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Lock className="h-6 w-6 text-white" />
                    <div>
                      <h2 className="text-xl font-semibold text-white">
                        Permissions du rôle
                      </h2>
                      <p className="text-blue-100 mt-1">
                        Sélectionnez les permissions accordées à ce rôle
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-300" />
                      <span className="text-white font-medium">
                        {selectedPermissions.length} permission(s) sélectionnée(s)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {loading ? (
                  <div className="flex flex-col justify-center items-center h-64 space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#202654] border-t-transparent"></div>
                    <p className="text-gray-500 font-medium">Chargement des permissions...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(allPermissions).map(([appKey, permissions]) => {
                      if (!permissions || permissions.length === 0) return null;
                      
                      const appName = appKey.replace('permissions_', '');
                      const IconComponent = getAppIcon(appName);
                      const isExpanded = expandedAccordions[appKey];
                      const selectedCount = getSelectedCount(permissions);
                      const allSelected = permissions.length > 0 && selectedCount === permissions.length;
                      const partiallySelected = selectedCount > 0 && selectedCount < permissions.length;
                      
                      return (
                        <div key={appKey} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                          <div
                            onClick={() => toggleAccordion(appKey)}
                            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200 bg-gradient-to-r from-gray-50 to-gray-100"
                          >
                            <div className="flex items-center space-x-4">
                              <div className={`p-3 rounded-lg ${allSelected ? 'bg-green-100' : partiallySelected ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                                <IconComponent className={`h-6 w-6 ${allSelected ? 'text-green-600' : partiallySelected ? 'text-yellow-600' : 'text-[#202654]'}`} />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {getAppDisplayName(appName)}
                                </h3>
                                <p className="text-sm text-gray-500">
                                  {selectedCount} sur {permissions.length} permissions sélectionnées
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectAllApp(permissions);
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                  allSelected 
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                    : 'bg-[#202654] text-white hover:bg-[#1a1f45]'
                                }`}
                              >
                                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                              </button>
                              <div className="flex items-center space-x-2">
                                {allSelected && <CheckCircle className="h-5 w-5 text-green-500" />}
                                {partiallySelected && <AlertCircle className="h-5 w-5 text-yellow-500" />}
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="border-t border-gray-200 p-6 bg-white">
                              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {permissions.map((permission) => {
                                  const isSelected = selectedPermissions.includes(permission.id);
                                  return (
                                    <label
                                      key={permission.id}
                                      className={`group flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                        isSelected 
                                          ? 'border-[#202654] bg-blue-50 shadow-md' 
                                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handlePermissionChange(permission.id)}
                                        className="h-5 w-5 text-[#202654] focus:ring-[#202654] border-gray-300 rounded mt-0.5"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-medium ${isSelected ? 'text-[#202654]' : 'text-gray-900'} group-hover:text-[#202654] transition-colors`}>
                                          {permission.name}
                                        </div>
                                      </div>
                                      {isSelected && (
                                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Actions de bas de page */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="text-sm text-gray-500">
                  {selectedPermissions.length > 0 ? (
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>
                        {selectedPermissions.length} permission(s) sélectionnée(s) sur {
                          Object.values(allPermissions).reduce((total, perms) => total + perms.length, 0)
                        } disponibles
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      <span>Aucune permission sélectionnée</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/utilisateurs')}
                    className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-all duration-200"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#1a1f45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isEditing ? 'Modifier le rôle' : 'Créer le rôle'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateRolePage;
